import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

const editableFields = [
  'name', 'surname', 'email', 'contact', 'gender', 'dob',
  //'password',  // removed
  'rating', 'role', 'status',
  'address', 'city', 'state', 'country', 'pincode',
  // 'roles' will be handled specially
];

function AdminUpdateAccounts() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [rolesList, setRolesList] = useState([]);  // new: roles array
  const [rolesToAdd, setRolesToAdd] = useState([]);
  const [rolesToRemove, setRolesToRemove] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API_URL}/list_users_data/`);
        const sorted = response.data.sort((a, b) => a.name.localeCompare(b.name));
        setUsers(sorted);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch users');
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, [loading]);

  const handleUserClick = (user) => {
    setSelectedUser(user);
    const values = {};
    editableFields.forEach((field) => {
      values[field] = user[field] || '';
    });
    setFormValues(values);
    // set roles
    const current = Array.isArray(user.roles) ? user.roles : [];
    setRolesList(current);
    setRolesToAdd([]);
    setRolesToRemove([]);
  };

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddRole = () => {
    const role = prompt('Enter role to add:').trim();
    if (!role) return;
    if (rolesList.includes(role)) {
      alert('Role already exists');
      return;
    }
    setRolesList((prev) => [...prev, role]);
    setRolesToAdd((prev) => [...prev, role]);
    // If it was scheduled for removal, undo remove
    setRolesToRemove((prev) => prev.filter(r => r !== role));
  };

  const handleRemoveRole = (role) => {
    if (!rolesList.includes(role)) return;
    setRolesList((prev) => prev.filter(r => r !== role));
    // If this role existed originally, schedule removal
    if (selectedUser.roles && selectedUser.roles.includes(role)) {
      setRolesToRemove((prev) => [...prev, role]);
    }
    // Also if it was new add, un-schedule add
    setRolesToAdd((prev) => prev.filter(r => r !== role));
  };

  const handleSubmit = async () => {
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }

    // Convert empty strings to null
    const cleanedValues = {};
    for (const key in formValues) {
      cleanedValues[key] = formValues[key] === '' ? null : formValues[key];
    }

    // Include roles update
    const rolesUpdate = {};
    if (rolesToAdd.length > 0) rolesUpdate.add = rolesToAdd;
    if (rolesToRemove.length > 0) rolesUpdate.remove = rolesToRemove;
    // Only include if there is any change
    if (Object.keys(rolesUpdate).length > 0) {
      cleanedValues.roles = rolesUpdate;
    }

    const payload = {
      user_identifier: selectedUser.email,
      updates: cleanedValues,
    };

    try {
      await axios.post(`${API_URL}/update_user_accounts/`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      showAlert();
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const showAlert = () => {
    const alertDiv = document.createElement('div');
    alertDiv.textContent = 'User updated successfully';
    alertDiv.style.position = 'fixed';
    alertDiv.style.top = '10px';
    alertDiv.style.left = '50%';
    alertDiv.style.transform = 'translateX(-50%)';
    alertDiv.style.padding = '10px 20px';
    alertDiv.style.backgroundColor = 'green';
    alertDiv.style.color = 'white';
    alertDiv.style.borderRadius = '5px';
    alertDiv.style.zIndex = '9999';
    alertDiv.style.fontSize = '16px';
    document.body.appendChild(alertDiv);
    setTimeout(() => alertDiv.remove(), 3000);
  };

  const filteredUsers = users.filter((u) =>
    `${u.name} ${u.surname} ${u.email} ${u.contact} ${u.city}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  if (loading) return <CircularProgress color="inherit" />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ bgcolor: '#000000ff', minHeight: '100vh', color: '#fff' }}>
  <Header1 />
  <Box sx={{ px: 3, pt: 6, pb: 4 }}>
    <Typography
      variant="h3"
      align="center"
      fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
      sx={{
        backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        fontWeight: 700,
        letterSpacing: 6,
      }}
    >
      Update User Accounts
    </Typography>

    <Typography align="center" sx={{ mt: 2, mb: 4, color: '#ccc' }}>
      Total Users: <strong>{filteredUsers.length}</strong>
    </Typography>

    <TextField
      inputRef={searchRef}
      fullWidth
      placeholder="Search by name, email, contact, city..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      variant="outlined"
      size="small"
      sx={{
        mb: 2,
        input: { color: '#fff', backgroundColor: '#1e1e1e', borderRadius: 1 },
        '& .MuiOutlinedInput-root': {
          '& fieldset': { borderColor: '#444' },
          '&:hover fieldset': { borderColor: '#888' },
          '&.Mui-focused fieldset': { borderColor: '#00bfff' },
        },
      }}
      InputLabelProps={{ style: { color: '#ccc' } }}
    />

    <Paper
      elevation={4}
      sx={{
        maxHeight: 300,
        overflowY: 'auto',
        backgroundColor: '#1e1e1e',
        p: 2,
        mb: 4,
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ color: '#ddd' }}>
        Click on a user to edit:
      </Typography>

      {filteredUsers.length === 0 ? (
        <Typography sx={{ color: '#888' }}>No users found.</Typography>
      ) : (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {filteredUsers.map((user) => (
            <Box
              key={user.id}
              flex="1 1 calc(33.333% - 8px)" // 3 per row
              minWidth="200px"
            >
              <Button
                fullWidth
                variant={selectedUser?.id === user.id ? 'contained' : 'outlined'}
                color="secondary"
                onClick={() => handleUserClick(user)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: '#fff',
                  borderColor: '#888',
                  bgcolor: selectedUser?.id === user.id ? '#333' : 'transparent',
                  '&:hover': { bgcolor: '#2a2a2a' },
                }}
              >
                {user.name} {user.surname}
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Paper>

    {selectedUser && (
      <>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Editing: <strong>{selectedUser.name} {selectedUser.surname}</strong>
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={2}>
          {editableFields.map((field) => (
            <Box key={field} flex="1 1 calc(33.333% - 16px)" minWidth="200px">
              <TextField
                fullWidth
                label={field.charAt(0).toUpperCase() + field.slice(1)}
                variant="outlined"
                value={formValues[field]}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                InputLabelProps={{ style: { color: '#bbb' } }}
                InputProps={{
                  style: {
                    color: '#fff',
                    backgroundColor: '#1e1e1e',
                    borderRadius: 4,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#444' },
                    '&:hover fieldset': { borderColor: '#888' },
                    '&.Mui-focused fieldset': { borderColor: '#00bfff' },
                  },
                }}
              />
            </Box>
          ))}

          {/* Roles editing UI */}
          <Box flex="1 1 100%">
            <Typography variant="subtitle1">Roles</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
              {rolesList.map((r) => (
                <Chip
                  key={r}
                  label={r}
                  onDelete={() => handleRemoveRole(r)}
                  sx={{ backgroundColor: '#333', color: '#fff' }}
                />
              ))}
            </Stack>
            <Button variant="outlined" onClick={handleAddRole}>
              Add Role
            </Button>
          </Box>

          <Box flex="1 1 100%">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              fullWidth
              sx={{ mt: 3 }}
            >
              Save All Changes
            </Button>
          </Box>
        </Box>
      </>
    )}
  </Box>
  <Footer2 />
</Box>

  );
}

export default AdminUpdateAccounts;
